import "./vendor/bootstrap-select/dist/css/bootstrap-select.min.css";
import "./css/style.css";
import "./jsx/index.css";
import "./css/tailwind-styles.css";

import { Redirect, Route, Switch } from "react-router-dom";
import { Suspense, lazy, useEffect, useState } from "react";

import AuthService from "./services/auth.service";
import BulletInvoiceTemplate from "./jsx/pages/trybz_dashboard/admin/billing&Payments/_components/invoice-template/BulletInvoiceTemplate";
import ChatService from "./services/chat.firebase.service";
import Cookies from "js-cookie";
import CustomerProfileComplete from "./jsx/pages/CustomerProfileComplete";
import CustomerProfileLogin from "./jsx/pages/CustomerProfileLogin";
import CustomerProfileSetup from "./jsx/pages/CustomerProfileSetup";
import ForceReloadModal from "./jsx/components/ForceReload";
import { InvitePage } from "./jsx/pages/invite/InvitePage";
import ItemizedInvoiceTemplate from "./jsx/pages/trybz_dashboard/admin/billing&Payments/_components/invoice-template/ItemizedInvoiceTemplate";
import MRTDEMO from "./jsx/pages/MRTDEMO";
import ParagraphInvoiceTemplate from "./jsx/pages/trybz_dashboard/admin/billing&Payments/_components/invoice-template/ParagraphInvoiceTemplate";
import Roles from "./enum/Role";
import { SelectActiveRole } from "./utils/helpers/selectRole";
import UserService from "./services/user.service";
import ViewInvoiceReceiptPage from "./jsx/pages/trybz_dashboard/admin/billing&Payments/viewInvoiceReceiptTemplates/ViewInvoiceReceiptPage";
import ViewManualReceiptPage from "./jsx/pages/trybz_dashboard/admin/billing&Payments/viewInvoiceReceiptTemplates/ViewManualReceiptPage";
import fetchAndSetInterswitchBillers from "./helpers/fetchAndSetInterswitchBillers";
import { getInterswitchBillers } from "./store/interswitchData.store";
import { history } from "./index";
import moment from "moment";
import { queryClient } from "./index";
import { socket } from "./socket";
import { useBulkActionStore } from "./store/bulkAction.store";
import { useCommunitiesStore } from "./store/communities.store";
import { useUserStore } from "./store/user.store";

// import Swal from "sweetalert2";

const Swal = lazy(() => import("sweetalert2"));

const ProfileComplete = lazy(() => import("./jsx/pages/ProfileComplete"));
const UpdateNationalId = lazy(() => import("./jsx/pages/UpdateNationalId"));
const Login = lazy(() => import("./jsx/pages/Login"));
const UpdatePhone = lazy(() => import("./jsx/pages/UpdatePhone"));
const UpdatePassword = lazy(() => import("./jsx/pages/UpdatePassword"));

const ChangePassword = lazy(() => import("./jsx/pages/ChangePassword"));
const VerifyOtp = lazy(() => import("./jsx/pages/VerifyOtp"));
const OnboardingManager = lazy(() => import("./jsx/pages/CompleteProfile"));
const ForgotPassword = lazy(() => import("./jsx/pages/ForgotPassword"));
const ChooseAccount = lazy(() => import("./jsx/pages/ChooseAccount"));
const ResetPassword = lazy(() => import("./jsx/pages/ResetPassword"));
const UpdateEmail = lazy(() => import("./jsx/pages/UpdateEmail"));
const PublicViewInvoiceDetails = lazy(() =>
  import("./jsx/pages/PublicViewInvoiceDetails")
);
const Index = lazy(() => import("./jsx/index"));

moment.updateLocale("en", {
  relativeTime: {
    future: "in %s",
    past: "%s ago",
    s: "a few seconds",
    m: "a minute",
    mm: "%d minutes",
    h: "an hour",
    hh: "%d hours",
    d: "a day",
    // dd: "%d days",
    dd: function (number) {
      if (number === 7) return `${Math.round(number / 7)} week`;
      else if (number > 7)
        return `${
          Math.round(number / 7) > 1
            ? `${Math.round(number / 7)} weeks`
            : `${Math.round(number / 7)} week`
        } ${number % 7 > 1 ? `${number % 7} days` : `${number % 7} day`}`;

      return `${number} days`;
    },
    w: "%d week",
    ww: "%d weeks",
    M: "a month",
    MM: "%d months",
    y: "a year",
    yy: "%d years",
  },
});

let publicPaths = [
  "/",
  "/login",
  "/choose-account",
  "/forgot-password",
  "/reset-password",
  "/verify-otp",
  "/change-password",
  "/complete-profile",
  "/profile-completed/success",
  "/update-email",
  "/update-phone",
  "/update-password",
  "/update-nationalId",
  "/invite",
  "/MRT-DEMO",
  "/customers/complete-profile",
  "/customers/complete-profile/success",
  "/customer-invite",
  "/invoice/template-bullet",
  "/invoice/template-itemized",
  "/invoice/template-paragraph",
  "/receipts/details",
  "/payment-info/details",
  // "/social/posts"
];

function App() {
  const accessToken = Cookies.get("access_token");
  const store = useUserStore.getState();
  const communitiesStore = useCommunitiesStore.getState();
  const tokenExpiresAt = Cookies.get("TrybzExpToken");
  const tokenExpiryDate = new Date(tokenExpiresAt);
  const hasInterswitchBillersSet = getInterswitchBillers().length > 0;
  const [isBlocked, setIsBlocked] = useState({
    isblocked: false,
    reason: "",
  });

  const [sessionUpdated, setSessionUpdated] = useState(false);

  const hasAuthenticatedViewAccess =
    accessToken &&
    store.isLoggedIn &&
    !publicPaths
      .filter((path) => path !== "/")
      .includes(window.location.pathname) &&
    !window.location.pathname.startsWith("/invite");

  // listen on websocket for userblocked event to display modal
  useEffect(() => {
    isBlocked.isblocked &&
      Swal.fire({
        title: "This account has been blocked.",
        text: `Reason:`,
        showConfirmButton: true,
        icon: "info",
      }).then(() => {
        AuthService.logout();
      });
  }, [isBlocked.isblocked]);

  useEffect(() => {
    accessToken && socket.connect();
    socket.on("user_blocked", ({ reason }) =>
      setIsBlocked({ isblocked: true, reason: reason })
    );
    return () => {
      socket.off("user_block");
    };
  }, [accessToken]);

  useEffect(() => {
    if (
      (!accessToken &&
        !publicPaths.includes(window.location.pathname) &&
        !window.location.pathname.startsWith("/invite")) ||
      (!accessToken && window.location.pathname.startsWith("/"))
    ) {
      useUserStore.setState({
        isLoggedIn: false,
      });
      // add callback url (previously tried to access a site but got redirected to login usecase) to url
      history.push("/login?return_url=" + window.location.pathname);
    } else if (accessToken && tokenExpiresAt && tokenExpiryDate > new Date()) {
      if (!store.user) {
        queryClient
          .fetchQuery(["user_info"], UserService.getUsersData)
          .then((data) => {
            const activeRole = SelectActiveRole(data.roles);
            useUserStore.setState({
              user: data,
              isLoggedIn: true,
              activeRole: activeRole,
            });
          })
          .then(() => {
            // Successful login actions here
            setTimeout(() => {
              ChatService.initialize();
            }, 50);
          })
          .catch((err) => {
            useUserStore.setState({
              isLoggedIn: false,
            });
            window.location = "/login";
          });
      } else if (
        store.activeRole !== Roles.Admin &&
        !communitiesStore.communityId
      ) {
        // if is customer avoid redirection to choose-account
        if (store.user.type === "customer") return;

        if (store.user.profiles?.length === 1) {
          const profiles = store.user?.profiles[0];
          useCommunitiesStore.setState({
            communityId: profiles.community.id,
            communityData: profiles,
          });
        } else {
          console.log(useCommunitiesStore.getState());
          history.push("/choose-account");
        }
      }
    } else if (accessToken && tokenExpiresAt && tokenExpiryDate < new Date()) {
      Cookies.remove("access_token");
      window.location = "/login";
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accessToken, store.isLoggedIn, store.activeRole]);

  useEffect(() => {
    // Listen to a change in the local storage
    window.addEventListener("storage", (event) => {
      if (event.key === "__user_id") {
        setSessionUpdated(true);
      }
    });
  }, []);

  useEffect(() => {
    const onlisten = history.listen((location, action) => {
      useUserStore.setState({ search: "", currentPath: location.pathname });
      if (location.pathname !== "/features/staff-management/print-cards") {
        useBulkActionStore.setState({
          bulkIds: [],
          bulkObjs: [],
          showCheck: false,
          readyForBulkAction: false,
          activateBulkAction: false,
          savedVisitorsObjects: [],
        });
      }
    });

    return onlisten;
  }, []);

  useEffect(() => {
    // if user has authenticated view access and interswitch store is not set.
    if (hasAuthenticatedViewAccess && !hasInterswitchBillersSet) {
      fetchAndSetInterswitchBillers();
    }
  }, [hasAuthenticatedViewAccess]);

  let routes = (
    <Switch>
      {/* <Route path="/testingdashboardtables" component={ChartsCombined} /> */}
      {/* login flow */}
      <Route path="/login" component={Login} />
      {/* forgot password flow */}
      <Route path="/forgot-password" component={ForgotPassword} />
      <Route path="/reset-password" component={ResetPassword} />
      {/* onboarding flow */}
      <Route path="/verify-otp" component={VerifyOtp} />
      <Route path="/change-password" component={ChangePassword} />
      <Route path="/complete-profile" component={OnboardingManager} />
      <Route path="/choose-account" component={ChooseAccount} />
      <Route path="/profile-completed/success" component={ProfileComplete} />
      <Route path="/profile-completed/success" component={ProfileComplete} />
      <Route
        path="/customers/complete-profile/success"
        component={CustomerProfileComplete}
      />
      {/* update routes */}
      <Route path="/update-email" component={UpdateEmail} />
      <Route path="/update-phone" component={UpdatePhone} />
      <Route path="/update-password" component={UpdatePassword} />
      <Route path="/update-nationalId" component={UpdateNationalId} />
      <Route path="/invite/:id" component={InvitePage} />
      <Route path="/customer-invite" component={CustomerProfileLogin} />
      <Route
        path="/bullet-invoice-template"
        component={BulletInvoiceTemplate}
      />

      <Route
        path={"/invoice/template-bullet"}
        component={BulletInvoiceTemplate}
      />
      <Route
        path={"/invoice/template-itemized"}
        component={ItemizedInvoiceTemplate}
      />
      <Route
        path={"/invoice/template-paragraph"}
        component={ParagraphInvoiceTemplate}
      />
      <Route
        path="/social/posts/:feedId"
        component={() => (
          <Redirect
            to={`/choose-account?return_url=${history.location.pathname}`}
          />
        )}
      />
      <Route
        path="social/posts/:feedId"
        component={() => (
          <Redirect
            to={`/choose-account?return_url=${history.location.pathname}`}
          />
        )}
      />

      {/* ID Card path */}
      <Route path="/MRT-DEMO" component={MRTDEMO} />
      <Route path="/receipts/details" component={ViewManualReceiptPage} />

      <Route
        path="/customers/complete-profile"
        component={CustomerProfileSetup}
      />
      <Route
        path="/payment-info/details"
        component={PublicViewInvoiceDetails}
      />
    </Switch>
  );

  if (hasAuthenticatedViewAccess) {
    return (
      <>
        <Suspense
          fallback={
            <div id="preloader">
              <div className="sk-three-bounce">
                <div className="sk-child sk-bounce1"></div>
                <div className="sk-child sk-bounce2"></div>
                <div className="sk-child sk-bounce3"></div>
              </div>
            </div>
          }
        >
          <Index />
          {sessionUpdated && (
            <ForceReloadModal onReload={() => window.location.reload()} />
          )}
        </Suspense>
      </>
    );
  } else {
    return (
      <div className="vh-100">
        <Suspense
          fallback={
            <div id="preloader">
              <div className="sk-three-bounce">
                <div className="sk-child sk-bounce1"></div>
                <div className="sk-child sk-bounce2"></div>
                <div className="sk-child sk-bounce3"></div>
              </div>
            </div>
          }
        >
          {routes}
        </Suspense>
      </div>
    );
  }
}

export default App;
